import { Component } from "@theme/component";

const METHOD_FRAGMENTS = new Set(["hot-brew", "iced-tea", "cold-brew"]);

/**
 * @typedef {Object} GuideFilterRefs
 * @property {HTMLButtonElement[]} tabs - Filter tab buttons (array ref `tabs[]`)
 * @property {HTMLElement[]} items - Filterable list items (array ref `items[]`)
 * @property {HTMLAnchorElement[]} productLinks - Product links that preserve the selected PDP format
 * @property {HTMLElement} status - Visually-hidden live region announcing the visible count
 */

/**
 * Client-side category filter and product deep-link enhancement for "Choose Your Tea".
 * The filter is self-contained: it has no URL sync, section re-render, or shared
 * event dispatch. A method fragment opens the first visible tea, while
 * a product fragment opens and re-scrolls its matching disclosure after component
 * upgrades. Without JS the tabs are hidden and all teas render.
 *
 * @extends {Component<GuideFilterRefs>}
 */
class GuideFilterComponent extends Component {
  requiredRefs = ["status"];

  /** @type {AbortController} */
  #controller = new AbortController();

  /** @type {string} */
  #category = "all";

  /** @type {number} */
  #deepLinkFrame = 0;

  /** @type {number} */
  #scrollFrame = 0;

  #queueDeepLink = () => {
    globalThis.cancelAnimationFrame(this.#deepLinkFrame);
    globalThis.cancelAnimationFrame(this.#scrollFrame);
    this.#deepLinkFrame = globalThis.requestAnimationFrame(() => {
      const fragment = globalThis.location.hash.slice(1);
      if (!fragment) {
        return;
      }

      const { items = [] } = this.refs;
      const isMethodFragment = METHOD_FRAGMENTS.has(fragment);
      const target = isMethodFragment ? items.find(item => !item.hidden) : items.find(item => item.id === fragment);
      if (!target) {
        return;
      }

      if (target.hidden) {
        this.#category = "all";
        this.#apply(false);
      }

      const details = target.querySelector("details");
      if (details instanceof HTMLDetailsElement) {
        details.open = true;
      }

      if (isMethodFragment) {
        return;
      }

      this.#scrollFrame = globalThis.requestAnimationFrame(() => {
        target.scrollIntoView({ block: "start", behavior: "auto" });
      });
    });
  };

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    globalThis.addEventListener("hashchange", this.#queueDeepLink, { signal: this.#controller.signal });
    this.#apply(false);
    this.#applySelectedFormat();
    this.#queueDeepLink();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    globalThis.cancelAnimationFrame(this.#deepLinkFrame);
    globalThis.cancelAnimationFrame(this.#scrollFrame);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#apply(false);
    this.#applySelectedFormat();
    this.#queueDeepLink();
  }

  /**
   * `on:click="/filter/<category>"` handler — the path segment arrives as the first arg.
   * @param {string} category - "all" | "black" | "oolong"
   */
  filter(category) {
    this.#category = category;
    this.#apply();
  }

  /**
   * @param {boolean} [announce=true] - Whether to announce the visible result count
   */
  #apply(announce = true) {
    const { tabs = [], items = [] } = this.refs;
    let count = 0;

    for (const item of items) {
      const show = this.#category === "all" || item.dataset.category === this.#category;
      item.hidden = !show;
      if (show) {
        count += 1;
      }
    }

    for (const tab of tabs) {
      const selected = tab.dataset.category === this.#category;
      tab.classList.toggle("is-selected", selected);
      tab.setAttribute("aria-pressed", String(selected));
    }

    if (announce) {
      this.refs.status.textContent = (this.dataset.countTemplate ?? "[count]").replace("[count]", String(count));
    }
  }

  #applySelectedFormat() {
    const format = new URL(globalThis.location.href).searchParams.get("tea_format");
    if (format !== "tea-sachets" && format !== "loose-leaf") {
      delete this.dataset.selectedFormat;
      return;
    }

    this.dataset.selectedFormat = format;
    const { productLinks = [] } = this.refs;
    for (const link of productLinks) {
      const formatUrl = format === "loose-leaf" ? link.dataset.looseLeafUrl : link.dataset.teaSachetsUrl;
      if (formatUrl) {
        link.href = formatUrl;
      }
    }
  }
}

if (!customElements.get("guide-filter-component")) {
  customElements.define("guide-filter-component", GuideFilterComponent);
}

export { GuideFilterComponent };
